import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Card, CardHeader, PageHeader, Skeleton } from '../components/ui';
import { Icon } from '../components/Icon';

interface RoleMatrix {
  permissions: { id: string; label: string }[];
  roles: { id: string; label: string; description: string; permissions: string[] }[];
}

/**
 * 角色与权限矩阵。
 *
 * 这份矩阵直接由 Worker 从 `shared/roles.ts` 生成 —— 界面上看到的
 * 与实际执行的判断来自同一个常量，不会出现「文档说可以、实际被拒」。
 */
export function RolesPage() {
  const matrix = useAsync<RoleMatrix>(() => api.get('/api/org/roles'), []);

  return (
    <div className="space-y-5">
      <PageHeader
        title="角色与权限"
        description="角色在组织内生效。同一个账号在不同组织里可以有不同的角色。"
      />

      <Card>
        <CardHeader title="权限矩阵" hint="打勾表示该角色拥有对应权限" />
        {matrix.loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] border-collapse text-left">
              <caption className="sr-only">角色与权限对应关系</caption>
              <thead>
                <tr className="bg-nest-2">
                  <th scope="col" className="px-3 py-2 text-xs font-medium text-fg-muted">
                    权限
                  </th>
                  {matrix.data?.roles.map((role) => (
                    <th
                      key={role.id}
                      scope="col"
                      className="px-3 py-2 text-center text-xs font-medium text-fg-muted"
                    >
                      {role.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.data?.permissions.map((permission) => (
                  <tr key={permission.id} className="border-t border-line">
                    <th scope="row" className="px-3 py-3 text-left text-[13px] font-normal text-fg">
                      {permission.label}
                      <span className="ml-2 font-mono text-[11px] text-fg-subtle">{permission.id}</span>
                    </th>
                    {matrix.data?.roles.map((role) => (
                      <td key={role.id} className="px-3 py-3 text-center">
                        {role.permissions.includes(permission.id) ? (
                          <Icon name="check" className="mx-auto h-4 w-4 text-ok" />
                        ) : (
                          <span className="text-fg-subtle">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        {matrix.data?.roles.map((role) => (
          <Card key={role.id}>
            <CardHeader title={role.label} />
            <p className="text-[13px] text-fg-muted">{role.description}</p>
            <p className="mt-3 text-xs text-fg-subtle">
              共 {role.permissions.length} 项权限
            </p>
          </Card>
        ))}
      </div>
    </div>
  );
}
